import React from 'react';
import { Button, Switch, TextField } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import withShipment from '../../withShipment';
import { userInfoSelector } from '../../redux/selectors/auth';
import { ticketPhotoSettingSelector } from '../../redux/selectors/ticketOut';
import {
  getTicketPhotoSetting,
  saveTicketPhotoSetting,
} from '../../redux/actions/ticketOut';
import { TICKET_THRESHOLD_PRESETS } from './constants';
import './TicketOut.css';

// Ticket Settings — Ticket Photo Requirement (spec §3).
// The gear icon and the back arrow live in the parent (TicketOut) toolbar;
// this component renders only the settings card.
class TicketOutSettings extends React.Component {
  constructor(props) {
    super(props);
    const setting = props.setting || {};
    this.state = {
      enabled: setting.enabled != null ? setting.enabled : true,
      threshold:
        setting.thresholdAmount != null ? String(setting.thresholdAmount) : '250',
      saving: false,
    };
  }

  componentDidMount() {
    // Re-fetch so the screen always reflects the latest persisted value
    // (it may have been changed on another device).
    this.props.getTicketPhotoSetting({
      locationId: this.props.user.selectedLocationId,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.setting !== this.props.setting && this.props.setting) {
      this.setState({
        enabled:
          this.props.setting.enabled != null ? this.props.setting.enabled : true,
        threshold:
          this.props.setting.thresholdAmount != null
            ? String(this.props.setting.thresholdAmount)
            : '250',
      });
    }
  }

  handleToggle = () => {
    this.setState((prev) => ({ enabled: !prev.enabled }));
  };

  handleThresholdChange = (event) => {
    const { value } = event.target;
    // Whole, non-negative dollars only (spec §3.1).
    if (value === '' || /^[0-9]+$/.test(value)) {
      this.setState({ threshold: value });
    }
  };

  setPreset = (value) => {
    this.setState({ threshold: String(value) });
  };

  save = () => {
    if (this.state.saving) return;
    this.setState({ saving: true });

    const callback = (error, message) => {
      this.setState({ saving: false });
      if (this.props.openFeedbackBar) {
        this.props.openFeedbackBar(error ? 'error' : 'success', message);
      }
    };

    this.props.saveTicketPhotoSetting(
      {
        locationId: this.props.user.selectedLocationId,
        enabled: this.state.enabled,
        thresholdAmount: Number(this.state.threshold || 0),
      },
      callback
    );
  };

  render() {
    const { enabled, threshold, saving } = this.state;
    const previewAmount = Number(threshold || 0).toLocaleString();

    return (
      <div className="ticket-settings-wrap">
        <div className="ticket-settings-card">
          <div className="ticket-settings-card-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <PhotoCameraIcon style={{ color: '#475569' }} />
              <div>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#0f172a',
                  }}
                >
                  Ticket Photo Requirement
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
                  For Ticket Out (Owner) entries
                </p>
              </div>
            </div>
            <Switch
              color="primary"
              checked={enabled}
              onChange={this.handleToggle}
            />
          </div>

          {enabled && (
            <div className="ticket-settings-card-body">
              <p className="ticket-settings-helper">
                When an employee enters a ticket at or above this amount, the
                system will require them to take a photo before saving.
              </p>

              <TextField
                variant="outlined"
                label="Minimum Amount"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={threshold}
                onChange={this.handleThresholdChange}
                type="tel"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  min: '0',
                  step: '1',
                }}
              />

              <p
                style={{
                  fontSize: 12,
                  color: '#64748b',
                  margin: '16px 0 0 0',
                }}
              >
                Quick presets
              </p>
              <div className="ticket-settings-pills">
                {TICKET_THRESHOLD_PRESETS.map((preset) => (
                  <button
                    type="button"
                    key={preset}
                    className={
                      'ticket-settings-pill' +
                      (Number(threshold) === preset ? ' active' : '')
                    }
                    onClick={() => this.setPreset(preset)}
                  >
                    ${preset.toLocaleString()}
                  </button>
                ))}
              </div>

              <div className="ticket-settings-preview">
                <p>
                  <strong>Preview:</strong> Tickets of ${previewAmount} or more
                  will require a photo.
                </p>
              </div>
            </div>
          )}

          <div className="ticket-settings-card-body" style={{ paddingTop: 0 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={this.save}
              disabled={saving}
            >
              {saving ? 'SAVING...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: userInfoSelector(state),
  setting: ticketPhotoSettingSelector(state),
});

const actionCreators = {
  getTicketPhotoSetting,
  saveTicketPhotoSetting,
};

export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  TicketOutSettings
);
